import type { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { FormActions } from "@jbnl/shared-ui";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Skeleton,
  SkeletonLine,
  SkeletonRegion,
} from "@/components/ui/skeleton";
import {
  useBezetting,
  useSetBezetting,
  useStamKlassen,
  useStamNiveaus,
  SCOPE,
  type Bezetting,
  type Functie,
  type KlasseStam,
  type NiveauStam,
} from "./useStamgegevens";

type Cel = { klasseId: string; niveauId: string };

const sleutel = (klasseId: string, niveauId: string) =>
  `${klasseId}|${niveauId}`;

/** What the server holds for this functie, as a set of keys - one per klasse/niveau pair, or per niveau on a dag functie. */
function opgeslagenSleutels(
  bezetting: Bezetting | undefined,
  functieId: string,
  isBaan: boolean,
): Set<string> {
  if (!bezetting) return new Set<string>();

  return isBaan
    ? new Set(
        bezetting.klasseNiveauFuncties
          .filter((x) => x.functieId === functieId)
          .map((x) => sleutel(x.klasseId, x.niveauId)),
      )
    : new Set(
        bezetting.niveauFuncties
          .filter((x) => x.functieId === functieId)
          .map((x) => x.niveauId),
      );
}

/** The chosen keys, shaped into what the save endpoint expects for a baan functie. */
function naarCellen(gekozen: Set<string>, isBaan: boolean): Cel[] {
  if (!isBaan) return [];

  return [...gekozen].map((k) => {
    const [klasseId, niveauId] = k.split("|");
    return { klasseId, niveauId };
  });
}

/**
 * The matrix ADR-0021 keys on klasse *and* niveau. A grid, because "needed here but not there"
 * is read across a row. Ticking does not save: a request per box would write a dozen audit rows
 * for one decision and leave the matrix half-edited if the third failed.
 */
export function BezettingGrid({ functie }: { functie: Functie }) {
  const { t } = useTranslation(["stamgegevens", "common"]);
  const { data: klassen } = useStamKlassen();
  const { data: niveaus } = useStamNiveaus();
  const { data: bezetting, isPending } = useBezetting();
  const opslaan = useSetBezetting();

  const isBaan = functie.scope === SCOPE.wedstrijdbaan;

  const opgeslagen = useMemo(
    () => opgeslagenSleutels(bezetting, functie.id, isBaan),
    [bezetting, functie.id, isBaan],
  );

  // `null` is "nothing ticked yet", and it reads through to whatever the server holds. Keeping
  // the unedited state absent rather than copying the server's answer into it is what lets a
  // refetch land without an effect to re-seed from - and an effect that assigns state is both a
  // cascading render and the thing that would clobber a half-made edit.
  const [bewerkt, setBewerkt] = useState<Set<string> | null>(null);
  const gekozen = bewerkt ?? opgeslagen;

  const vuil =
    bewerkt !== null &&
    (bewerkt.size !== opgeslagen.size ||
      [...bewerkt].some((k) => !opgeslagen.has(k)));

  function wissel(key: string) {
    setBewerkt((vorige) => {
      const volgende = new Set(vorige ?? opgeslagen);
      if (!volgende.delete(key)) volgende.add(key);
      return volgende;
    });
  }

  function bewaar() {
    opslaan.mutate(
      {
        functieId: functie.id,
        cellen: naarCellen(gekozen, isBaan),
        niveauIds: isBaan ? [] : [...gekozen],
      },
      // Back to reading through to the server, which is now the same set - so the button
      // disables itself rather than staying lit over an edit that has already landed.
      { onSuccess: () => setBewerkt(null) },
    );
  }

  if (isPending || !klassen || !niveaus) {
    // The scope is the functie's own, so the matrix already knows whether it is one row or
    // several before a single klasse or niveau has arrived.
    return <BezettingSkeleton rijen={isBaan ? 4 : 1} />;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {isBaan ? t("bezetting.uitlegBaan") : t("bezetting.uitlegDag")}
      </p>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">
            {t("bezetting.caption", { functie: functie.name })}
          </caption>
          <thead>
            <tr>
              <th scope="col" className="w-48 py-2 text-left font-medium">
                {isBaan ? t("bezetting.klasse") : ""}
              </th>
              {niveaus.map((niveau) => (
                <th
                  key={niveau.id}
                  scope="col"
                  className="px-3 py-2 text-left font-medium"
                >
                  {niveau.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isBaan ? (
              klassen.map((klasse) => (
                <BaanRij
                  key={klasse.id}
                  klasse={klasse}
                  niveaus={niveaus}
                  functie={functie}
                  gekozen={gekozen}
                  wissel={wissel}
                  t={t}
                />
              ))
            ) : (
              <DagRij
                niveaus={niveaus}
                functie={functie}
                gekozen={gekozen}
                wissel={wissel}
                t={t}
              />
            )}
          </tbody>
        </table>
      </div>

      <FormActions>
        {/* Left of the button, so appearing or clearing never shifts what the reader is aiming at. */}
        {opslaan.error && (
          <p className="text-sm text-destructive">{opslaan.error.message}</p>
        )}
        {vuil && !opslaan.isPending && (
          <span className="text-sm text-muted-foreground">
            {t("bezetting.vuil")}
          </span>
        )}
        <Button onClick={bewaar} disabled={!vuil || opslaan.isPending}>
          {opslaan.isPending
            ? t("common:actions.busy")
            : t("bezetting.opslaan")}
        </Button>
      </FormActions>

      {gekozen.size === 0 && (
        <p className="text-sm">
          <Trans
            t={t}
            i18nKey="bezetting.nergens"
            components={{ em: <em /> }}
          />
        </p>
      )}
    </div>
  );
}

type RijProps = {
  niveaus: NiveauStam[];
  functie: Functie;
  gekozen: Set<string>;
  wissel: (key: string) => void;
  t: TFunction<["stamgegevens", "common"]>;
};

/** One klasse's row: a checkbox per niveau, keyed on the klasse/niveau pair. */
function BaanRij({
  klasse,
  niveaus,
  functie,
  gekozen,
  wissel,
  t,
}: RijProps & { klasse: KlasseStam }) {
  return (
    <tr className="border-t">
      <th scope="row" className="py-2 text-left font-normal">
        {klasse.name}
      </th>
      {niveaus.map((niveau) => {
        const key = sleutel(klasse.id, niveau.id);
        return (
          <td key={niveau.id} className="px-3 py-2">
            <Checkbox
              checked={gekozen.has(key)}
              onCheckedChange={() => wissel(key)}
              aria-label={t("bezetting.vinkBaan", {
                functie: functie.name,
                klasse: klasse.name,
                niveau: niveau.name,
              })}
            />
          </td>
        );
      })}
    </tr>
  );
}

/** The one row a dag functie has: a checkbox per niveau, keyed on the niveau alone. */
function DagRij({ niveaus, functie, gekozen, wissel, t }: RijProps) {
  return (
    <tr className="border-t">
      <th scope="row" className="py-2 text-left font-normal">
        {t("bezetting.heleDag")}
      </th>
      {niveaus.map((niveau) => (
        <td key={niveau.id} className="px-3 py-2">
          <Checkbox
            checked={gekozen.has(niveau.id)}
            onCheckedChange={() => wissel(niveau.id)}
            aria-label={t("bezetting.vinkDag", {
              functie: functie.name,
              niveau: niveau.name,
            })}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * The matrix before the klassen and niveaus behind it have arrived. Four niveau columns is what
 * JBNL runs, so the header row lands where it will stay for all but the first render.
 */
function BezettingSkeleton({ rijen }: { rijen: number }) {
  const kolommen = 4;

  return (
    <SkeletonRegion className="space-y-4">
      <SkeletonLine className="w-4/5" />

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <tbody>
            <SkeletonKopRij kolommen={kolommen} />
            {Array.from({ length: rijen }, (_, rij) => (
              <SkeletonRij key={rij} kolommen={kolommen} />
            ))}
          </tbody>
        </table>
      </div>

      <FormActions>
        <Skeleton className="h-9 w-40" />
      </FormActions>
    </SkeletonRegion>
  );
}

/** The header row: a name column plus one skeleton line per niveau. */
function SkeletonKopRij({ kolommen }: { kolommen: number }) {
  return (
    <tr>
      <td className="w-48 py-2">
        <SkeletonLine className="w-24" />
      </td>
      {Array.from({ length: kolommen }, (_, kolom) => (
        <td key={kolom} className="px-3 py-2">
          <SkeletonLine className="w-20" />
        </td>
      ))}
    </tr>
  );
}

/** One klasse or dag row: a name line plus one checkbox-shaped skeleton per niveau. */
function SkeletonRij({ kolommen }: { kolommen: number }) {
  return (
    <tr className="border-t">
      <td className="py-2">
        <SkeletonLine className="w-32" />
      </td>
      {Array.from({ length: kolommen }, (_, kolom) => (
        <td key={kolom} className="px-3 py-2">
          <Skeleton className="size-4 rounded-sm" />
        </td>
      ))}
    </tr>
  );
}
